import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { jobsAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { Plus, Edit, Trash2, Eye, Search, Briefcase, MapPin, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${p => p.theme.typography.fontSize['2xl']};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  color: ${p => p.theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0 0;
  color: ${p => p.theme.colors.text.secondary};
  font-size: ${p => p.theme.typography.fontSize.sm};
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const JobCard = styled(Card)`
  padding: 1.75rem;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  border: 1px solid ${p => p.theme.colors.border.light};

  &:hover {
    box-shadow: ${p => p.theme.shadows.md};
    border-color: ${p => p.theme.colors.primary}40;
    transform: translateY(-2px);
  }
`;

const JobHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 1rem;
`;

const JobInfo = styled.div`
  flex: 1;
`;

const JobTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: ${p => p.theme.colors.text.primary};
  font-size: ${p => p.theme.typography.fontSize.xl};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  line-height: 1.3;
`;

const JobSummary = styled.p`
  margin: 0.5rem 0 0 0;
  color: ${p => p.theme.colors.text.secondary};
  line-height: 1.6;
  font-size: ${p => p.theme.typography.fontSize.sm};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${p => p.theme.colors.border.light};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${p => p.theme.colors.text.secondary};
  font-size: ${p => p.theme.typography.fontSize.sm};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.875rem;
  border-radius: ${p => p.theme.borderRadius.full};
  font-size: ${p => p.theme.typography.fontSize.xs};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  background: ${p => p.active ? '#10B98120' : '#EF444420'};
  color: ${p => p.active ? '#065F46' : '#991B1B'};
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${p => p.theme.colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.3;
`;

const JobsManagement = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await jobsAPI.getAll({});
      setJobs(res.data.data || res.data || []);
    } catch (e) {
      setError('Failed to load jobs.');
      toast.error('Failed to load jobs.');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job posting? This action cannot be undone.')) return;
    setDeleteId(id);
    try {
      await jobsAPI.delete(id);
      toast.success('Job deleted successfully.');
      fetchJobs();
    } catch (e) {
      toast.error('Failed to delete job.');
      setError('Failed to delete job.');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredJobs = jobs.filter(job => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      job.title?.toLowerCase().includes(query) ||
      job.summary?.toLowerCase().includes(query) ||
      job.location?.toLowerCase().includes(query) ||
      job.employment_type?.toLowerCase().includes(query)
    );
  });

  return (
    <Container>
      <Header>
        <div>
          <Title theme={theme}>Jobs Management</Title>
          <Subtitle theme={theme}>
            Create and manage job postings for your organization
          </Subtitle>
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus size={16} />}
          onClick={() => navigate('/app/jobs/create')}
        >
          Create Job
        </Button>
      </Header>

      {jobs.length > 0 && (
        <SearchBar>
          <Input
            placeholder="Search jobs by title, location, or type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search size={16} />}
            style={{ flex: 1 }}
          />
        </SearchBar>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: theme.colors.text.secondary }}>
          Loading jobs...
        </div>
      ) : filteredJobs.length === 0 ? (
        <EmptyState theme={theme}>
          <EmptyIcon>💼</EmptyIcon>
          <h3 style={{ margin: '0 0 0.5rem 0', color: theme.colors.text.primary }}>
            {searchQuery ? 'No jobs found' : 'No jobs yet'}
          </h3>
          <p style={{ margin: '0 0 1.5rem 0' }}>
            {searchQuery
              ? 'Try adjusting your search terms'
              : 'Create your first job posting to get started'}
          </p>
          {!searchQuery && (
            <Button
              variant="primary"
              leftIcon={<Plus size={16} />}
              onClick={() => navigate('/app/jobs/create')}
            >
              Create Your First Job
            </Button>
          )}
        </EmptyState>
      ) : (
        <>
          {searchQuery && (
            <div style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm, marginBottom: '0.5rem' }}>
              Showing {filteredJobs.length} of {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
            </div>
          )}
          {filteredJobs.map(job => (
            <JobCard key={job.id} theme={theme}>
              <JobHeader>
                <JobInfo>
                  <JobTitle theme={theme}>{job.title}</JobTitle>
                  {job.summary && (
                    <JobSummary theme={theme}>{job.summary}</JobSummary>
                  )}
                  <MetaRow theme={theme}>
                    <StatusBadge active={job.is_active} theme={theme}>
                      {job.is_active ? (
                        <>
                          <CheckCircle size={14} />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircle size={14} />
                          Inactive
                        </>
                      )}
                    </StatusBadge>
                    {job.location && (
                      <MetaItem theme={theme}>
                        <MapPin size={16} />
                        <span>{job.location}</span>
                      </MetaItem>
                    )}
                    {job.employment_type && (
                      <MetaItem theme={theme}>
                        <Briefcase size={16} />
                        <span>{job.employment_type}</span>
                      </MetaItem>
                    )}
                    {job.deadline && (
                      <MetaItem theme={theme}>
                        <Calendar size={16} />
                        <span>Apply by {new Date(job.deadline).toLocaleDateString()}</span>
                      </MetaItem>
                    )}
                  </MetaRow>
                </JobInfo>
                <Actions>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => navigate(`/app/jobs/${job.id}/applications`)}
                    leftIcon={<Eye size={14} />}
                  >
                    Applications
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => navigate(`/app/jobs/${job.id}/edit`)}
                    leftIcon={<Edit size={14} />}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => confirmDelete(job.id)}
                    disabled={deleteId === job.id}
                    leftIcon={<Trash2 size={14} />}
                    style={{ color: theme.colors.error }}
                  >
                    {deleteId === job.id ? 'Deleting...' : 'Delete'}
                  </Button>
                </Actions>
              </JobHeader>
            </JobCard>
          ))}
        </>
      )}

      {error && (
        <div style={{
          padding: '1rem',
          background: theme.colors.error + '15',
          color: theme.colors.error,
          borderRadius: theme.borderRadius.md,
          fontSize: theme.typography.fontSize.sm
        }}>
          {error}
        </div>
      )}
    </Container>
  );
};

export default JobsManagement;
